// =============================================================================
// "Where you are" — scroll-synced step progress for the article right sidebar.
// Measurement ported 1:1 from behavior-spec §2:
//   mark = 200px reading line; scrolled to bottom → 100%; first step below
//   0.85 × viewport → 0%; per step top ≤ mark = started, bottom ≤ mark =
//   passed; pct = ((active−1) + fraction of active) / N × 100.
// The live pct drives the sidebar UI; the store keeps the monotonic max per
// article slug so the left nav and setup card hydrate from it.
// =============================================================================

import { recordPct } from "./progress-store.js";
import { readTree } from "./guide-data.js";

const MARK = 200; // px reading line from viewport top

function initWhereBox() {
	const box = document.querySelector("[data-aa-where]");
	const section = document.querySelector(
		".wp-block-epos-academy-steps-section"
	);
	if (!box || !section) {
		return;
	}

	const list = box.querySelector(".aa-where__list");
	const fill = box.querySelector(".aa-where__fill");
	const pct = box.querySelector(".aa-where__pct");
	const tree = readTree();
	const slug = tree ? tree.current : null;

	let steps = [];

	const activePanel = () =>
		section.querySelector(".aa-steps__panel.is-active") ||
		section.querySelector(".aa-steps__panel");

	const buildList = () => {
		const panel = activePanel();
		steps = panel ? Array.from(panel.querySelectorAll(".aa-step")) : [];
		list.innerHTML = "";
		steps.forEach((step, i) => {
			const num = step.querySelector(".aa-step__num");
			const title = step.querySelector(".aa-step__title");
			const li = document.createElement("li");
			const btn = document.createElement("button");
			btn.className = "aa-where__item";
			btn.innerHTML =
				`<span class="aa-where__badge" aria-hidden="true">${num ? num.textContent.trim() : i + 1}</span>` +
				`<span class="aa-where__label">${title ? title.textContent.trim() : ""}</span>`;
			btn.addEventListener("click", () => {
				step.scrollIntoView({ behavior: "smooth", block: "start" });
			});
			li.appendChild(btn);
			list.appendChild(li);
		});
		update();
	};

	const measure = () => {
		const N = steps.length;
		if (!N) {
			return 0;
		}

		// Scrolled to page bottom (within 4px) → 100%, all steps passed.
		if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 4) {
			return 100;
		}

		// First step still below 0.85 × viewport → 0%.
		if (steps[0].getBoundingClientRect().top > window.innerHeight * 0.85) {
			return 0;
		}

		let passed = 0;
		let active = -1;
		let fraction = 0;
		steps.forEach((step, i) => {
			const r = step.getBoundingClientRect();
			if (r.bottom <= MARK) {
				passed = i + 1;
			}
			if (active === -1 && r.top <= MARK && r.bottom > MARK) {
				active = i + 1;
				fraction = Math.max(0, Math.min(1, (MARK - r.top) / Math.max(r.height, 1)));
			}
		});
		if (active === -1) {
			// Between steps: next after passed. May reach N+1 when every step
			// is passed — that is a full 100%, not (N-1)/N.
			active = passed + 1;
		}

		// Every step passed the reading line (e.g. scrolled on to the videos
		// section) → the run is complete, not (N-1)/N.
		if (passed === N) {
			return 100;
		}

		return Math.max(
			0,
			Math.min(100, Math.round((((active - 1) + fraction) / N) * 100))
		);
	};

	const update = () => {
		if (!steps.length) {
			return;
		}
		const p = measure();

		const atBottom = p === 100;
		const rects = steps.map((s) => s.getBoundingClientRect());
		const passed = atBottom
			? steps.length
			: rects.filter((r) => r.bottom <= MARK).length;
		let current = -1;
		rects.forEach((r, i) => {
			if (current === -1 && r.top <= MARK && r.bottom > MARK) {
				current = i;
			}
		});
		if (current === -1 && !atBottom) {
			current = passed < steps.length ? passed : -1;
		}

		Array.from(list.querySelectorAll(".aa-where__item")).forEach((item, i) => {
			item.classList.toggle("is-done", atBottom || i < passed);
			item.classList.toggle("is-current", i === current);
		});

		fill.style.width = `${p}%`;
		pct.textContent = `${p}%`;

		// Persist monotonic max for this article (drives nav % + setup card).
		recordPct(slug, p);
	};

	let ticking = false;
	const onScroll = () => {
		if (ticking) {
			return;
		}
		ticking = true;
		window.requestAnimationFrame(() => {
			update();
			ticking = false;
		});
	};

	// Rebuild the list when the device tab changes (view.js toggles panels).
	section.querySelectorAll(".aa-steps__tab").forEach((btn) => {
		btn.addEventListener("click", () => window.setTimeout(buildList, 0));
	});

	window.addEventListener("scroll", onScroll, { passive: true });
	window.addEventListener("resize", onScroll);

	buildList();
}

document.addEventListener("DOMContentLoaded", initWhereBox);
